import { create } from 'zustand';
import {
  getLarkIntegrations,
  LarkIntegrationRulesResponse,
  getIntegrationTrainingProgress,
  getDatasetGbaseProjects,
  IntegrationState,
  GbaseProject,
} from '@mygpt/app';

interface IntegrationRulesState {
  ruleData: LarkIntegrationRulesResponse;
  loading: boolean;
  error: Error | null;
  sortBy: string;
  reverseSortDirection: boolean;
  refreshStatusLoading: boolean;
  refreshStatus: (datasetId?: string) => Promise<void>;
  fetchRules: (params: {
    datasetId?: string;
    botId?: string;
    page?: number;
    size?: number;
    order_by?: string;
  }) => Promise<void>;
  updateRuleByID: (id: string, data: { name: string; sync_interval: number }) => void;
  setSorting: (field: string, datasetId?: string, botId?: string) => void;
  removeRuleByID: (id: string) => void;
  setRulesStatus: (status: Record<string, IntegrationState>) => void;
}

const DEFAULT_PAGE_SIZE = 10;

// 将 GbaseProject 转换成 IntegrationRule 格式
const convertGbaseProjectToIntegrationRule = (project: GbaseProject): IntegrationRule => {
  return {
    id: project.uuid,
    name: project.title,
    type: 'gbase_project' as any, // 标记为 GBase Project 类型
    detail: {
      share_urls: project.web_url ? [{ url: project.web_url }] : [],
      app_id: '',
      app_secret: '',
      notification_emails: [],
    },
    created_by: {
      id: '',
      name: '', // GBase Project 创建人留空
      email: '',
    },
    created_at: project.created_at || new Date().toISOString(), // 使用返回的创建时间，如果没有则使用当前时间
    file_count: project.item_count || 0,
    status: {
      complete: 0,
      learning: 0,
      failed: 0,
    },
    last_synced_at: '',
    sync_interval: 0, // GBase Project 没有同步间隔概念
  };
};

export const useIntegrationRuleStore = create<IntegrationRulesState>((set, get) => ({
  ruleData: {
    items: [],
    total: 0,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    pages: 0,
  },
  sortBy: '',
  reverseSortDirection: false,
  loading: false,
  error: null,
  refreshStatusLoading: false,

  fetchRules: async ({
    datasetId,
    botId,
    page = 1,
    size = DEFAULT_PAGE_SIZE,
    order_by = '',
  }: {
    datasetId?: string;
    botId?: string;
    page?: number;
    size?: number;
    order_by?: string;
  }) => {
    if (!datasetId) {
      console.error('datasetId is undefined');
      return;
    }

    const { loading, refreshStatus } = get();

    if (loading) return;

    set({ loading: true });
    try {
      // 获取 Lark 集成数据
      const { data } = await getLarkIntegrations(datasetId, {
        type: ['lark'],
        page,
        size,
        order_by,
      });

      let allItems = data.items.map((item) => ({
        ...item,
        sync_interval: item.sync_interval || 0,
      }));

      // 获取 GBase Projects 并合并
      try {
        const gbaseRes = await getDatasetGbaseProjects(datasetId);
        const gbaseProjects = gbaseRes.data || [];

        // 将 GBase Projects 转换成 IntegrationRule 格式
        const gbaseItems = gbaseProjects.map(convertGbaseProjectToIntegrationRule);

        // 合并两种数据（GBase Projects 排在前面）
        allItems = [...gbaseItems, ...allItems];
      } catch (error) {
        console.error('获取 GBase Projects 失败:', error);
        // 即使失败也继续，只显示 Lark 集成
      }

      set({
        ruleData: {
          ...data,
          items: allItems,
          total: allItems.length,
        },
        error: null,
      });

      refreshStatus(datasetId);
    } catch (err) {
      set({ error: err as Error });
    } finally {
      set({ loading: false });
    }
  },

  refreshStatus: async (datasetId?: string) => {
    const { refreshStatusLoading, setRulesStatus } = get();
    if (!datasetId) return;
    if (refreshStatusLoading) return;

    set({ refreshStatusLoading: true });

    try {
      const { data } = await getIntegrationTrainingProgress(datasetId);
      setRulesStatus(data.integration_state);
    } catch (error) {
      console.error(error);
    } finally {
      set({ refreshStatusLoading: false });
    }
  },

  setRulesStatus: (integrationState: Record<string, IntegrationState>) => {
    const { ruleData } = get();
    const newRuleItems = ruleData.items.map((rule) => {
      const progress = integrationState[rule.id];
      if (!progress) return rule;

      return {
        ...rule,
        status: progress.type_count,
      };
    });
    set({ ruleData: { ...ruleData, items: newRuleItems } });
  },

  updateRuleByID: (id: string, { name, sync_interval }) => {
    set((state) => {
      const targetIndex = state.ruleData.items.findIndex((item) => item.id === id);
      if (targetIndex === -1) return state;

      const updatedItems = [...state.ruleData.items];
      updatedItems[targetIndex] = {
        ...updatedItems[targetIndex],
        name,
        sync_interval,
      };

      return {
        ruleData: {
          ...state.ruleData,
          items: updatedItems,
        },
      };
    });
  },

  removeRuleByID: (id: string) => {
    set((state) => {
      const targetIndex = state.ruleData.items.findIndex((item) => item.id === id);
      if (targetIndex === -1) return state;

      const updatedItems = [...state.ruleData.items];
      updatedItems.splice(targetIndex, 1);

      return {
        ruleData: {
          ...state.ruleData,
          items: updatedItems,
        },
      };
    });
  },

  setSorting: (field: string, datasetId?: string, botId?: string) => {
    if (!datasetId) {
      console.error('datasetId is undefined');
      return;
    }

    const { sortBy, reverseSortDirection, fetchRules, ruleData } = get();

    const reversed = field === sortBy ? reverseSortDirection : false;
    set({ reverseSortDirection: reversed, sortBy: field });

    fetchRules({
      datasetId,
      botId,
      page: ruleData.page,
      size: ruleData.size,
      order_by: `${reversed ? '-' : ''}${field}`,
    });
  },
}));
